import { FC } from 'react';
import { Tooltip } from '../../components/ui/tooltip';
import { WarningIcon } from '@phosphor-icons/react';
import styles from './ComplaintBadge.module.scss';

export interface ComplaintDetail {
  zone: string;
  intensity: number;
  type: string;
  impact: string;
}

interface ComplaintBadgeProps {
  complaints?: ComplaintDetail[];
  maxIntensity?: number;
  size?: 'sm' | 'md';
}

const ZONE_LABELS: Record<string, string> = {
  head_neck: 'Testa/Collo',
  shoulder_dx: 'Spalla Dx',
  shoulder_sx: 'Spalla Sx',
  chest_ribs: 'Petto/Costole',
  elbow_dx: 'Gomito Dx',
  elbow_sx: 'Gomito Sx',
  wrist_hand_dx: 'Polso/Mano Dx',
  wrist_hand_sx: 'Polso/Mano Sx',
  quadricep_dx: 'Quadricipite Dx',
  quadricep_sx: 'Quadricipite Sx',
  knee_dx: 'Ginocchio Dx',
  knee_sx: 'Ginocchio Sx',
  upper_back: 'Dorsale Sup.',
  lumbar: 'Lombare',
  hip_groin_dx: 'Fianco/Inguine Dx',
  hip_groin_sx: 'Fianco/Inguine Sx',
  hamstring_dx: 'Hamstring Dx',
  hamstring_sx: 'Hamstring Sx',
  calf_dx: 'Polpaccio Dx',
  calf_sx: 'Polpaccio Sx',
  ankle_foot_dx: 'Caviglia/Piede Dx',
  ankle_foot_sx: 'Caviglia/Piede Sx',
};

const TYPE_LABELS: Record<string, string> = {
  pain: 'Dolore',
  stiffness: 'Rigidità',
  swelling: 'Gonfiore',
  weakness: 'Debolezza',
};

const IMPACT_LABELS: Record<string, string> = {
  can_do_all: 'Nessuna limit.',
  will_limit: 'Limitazione parziale',
  cannot_do_all: 'Non riesce a fare',
};

function getIntensityColor(intensity: number): string {
  if (intensity >= 4) return '#b91c1c';
  if (intensity >= 3) return '#c2410c';
  if (intensity >= 2) return '#d97706';
  return '#92400e';
}

export const ComplaintBadge: FC<ComplaintBadgeProps> = ({
  complaints,
  maxIntensity,
  size = 'sm',
}) => {
  const hasDetails = complaints && complaints.length > 0;
  const displayIntensity = hasDetails
    ? Math.max(...complaints.map((c) => c.intensity))
    : maxIntensity;

  const tooltipContent = (
    <div className={styles.tooltip}>
      <p className={styles.tooltipTitle}>
        Fastidi segnalati ({hasDetails ? complaints.length : '?'})
      </p>
      {hasDetails ? (
        complaints.map((c, i) => (
          <div key={i} className={styles.complaintItem}>
            <p className={styles.tooltipLine}>
              <span className={styles.tooltipKey}>Zona:</span>
              {ZONE_LABELS[c.zone] ?? c.zone}
            </p>
            <p className={styles.tooltipLine}>
              <span className={styles.tooltipKey}>Tipo:</span>
              {TYPE_LABELS[c.type] ?? c.type}
            </p>
            <p className={styles.tooltipLine}>
              <span className={styles.tooltipKey}>Intensità:</span>
              {c.intensity}/5
            </p>
            <p className={styles.tooltipLine}>
              <span className={styles.tooltipKey}>Impatto:</span>
              {IMPACT_LABELS[c.impact] ?? c.impact}
            </p>
          </div>
        ))
      ) : (
        displayIntensity != null && (
          <p className={styles.tooltipLine}>
            <span className={styles.tooltipKey}>Intensità max:</span>
            {displayIntensity}/5
          </p>
        )
      )}
    </div>
  );

  const color = getIntensityColor(displayIntensity ?? 2);

  return (
    <Tooltip content={tooltipContent}>
      <span
        className={[styles.badge, styles[size]].join(' ')}
        style={{ backgroundColor: `${color}18`, color }}
      >
        <WarningIcon size={size === 'sm' ? 10 : 12} weight="bold" />
        {hasDetails ? `${complaints.length} fastid${complaints.length === 1 ? 'io' : 'i'}` : 'Fastidio'}
      </span>
    </Tooltip>
  );
};

export default ComplaintBadge;
